<template>
    <div class="py-12">
        <h1
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ t("vue.app_env_vars") }}
        </h1>

        <div class="mt-4">
            <inertia-link :href="route('kiosk.app-env-vars.create')">
                <AtomicButton size="sm" color="yellow">
                    {{ t("vue.create") }}
                    <template #icon>
                        <PlusCircleIcon
                            class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </inertia-link>
        </div>

        <DataTable
            class="mt-10"
            :headers="headers"
            :items="appEnvVars"
            searchable
        >
            <template #item-id="{ data }">
                <div class="flex p-6 text-gray-500">
                    {{ data }}
                </div>
            </template>

            <template #item-key="{ data }">
                <div class="flex p-6 font-medium text-gray-800">
                    {{ data }}
                </div>
            </template>

            <template #item-value="{ data }">
                <div class="flex max-w-[300px] truncate p-6 text-gray-500">
                    {{ data }}
                </div>
            </template>

            <template #item-value_type="{ data }">
                <div class="flex p-6 text-gray-500">
                    <span
                        class="rounded bg-gray-100 px-2 py-1 text-xs font-medium uppercase text-gray-700"
                    >
                        {{ data }}
                    </span>
                </div>
            </template>

            <template #item-custom_edit="{ row }">
                <inertia-link
                    :href="
                        route('kiosk.app-env-vars.edit', {
                            appEnvVar: row.id as number,
                        })
                    "
                >
                    <AtomicButton size="sm" color="white">
                        <template #icon>
                            <PencilSquareIcon
                                class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                            />
                        </template>
                    </AtomicButton>
                </inertia-link>
            </template>

            <template #item-custom_delete="{ row }">
                <AtomicButton
                    size="sm"
                    color="red"
                    @click="deleteAppEnvVar(row as IAppEnvVar)"
                >
                    <template #icon>
                        <TrashIcon
                            class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import { router } from "@inertiajs/vue3";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import type IAppEnvVar from "@/types/AppEnvVars/IAppEnvVar";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";

defineProps<{
    appEnvVars: IPaginationData<IAppEnvVar>;
}>();

const { t } = useTranslations();
const { show } = useConfirm();

const headers: IDataTableHeader[] = [
    { text: t("vue.id"), id: "id", sortable: true, sortableId: "id" },
    { text: t("vue.key"), id: "key", sortable: true, sortableId: "key" },
    { text: t("vue.value"), id: "value" },
    {
        text: t("vue.value_type"),
        id: "value_type",
        sortable: true,
        sortableId: "value_type",
    },
    { text: "", id: "custom_edit" },
    { text: "", id: "custom_delete" },
];

const deleteAppEnvVar = async (appEnvVar: IAppEnvVar) => {
    const confirmed = await show({
        header: t("vue.delete"),
        message: t("vue.delete_app_env_var_message"),
        type: "danger",
        confirm: t("vue.delete"),
    });

    if (confirmed) {
        router.delete(
            route("kiosk.app-env-vars.destroy", {
                appEnvVar: appEnvVar.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: t("vue.app_env_var_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: t("vue.app_env_var_not_deleted"),
                        type: "error",
                    });
                },
            },
        );
    }
};
</script>
